/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import OverallSummaryValueQuery from './OverallSummaryValueQuery';
import FilteredSummaryValueQuery from './FilteredSummaryValueQuery';
import SummaryCollector from './SummaryCollector';
import SummarizerHostAPI from './SummarizerHostAPI';
import CombinedSummaryValidator from './CombinedSummaryValidator';
import CombinedSummaryValueQuery from './CombinedSummaryValueQuery';

/**
 * The SummarizerFeature is a plugin to the resultdatareader that is
 * responsible for collecting statistics for both default and selected data.
 * (Note: the slected data case is needed for the open case or when data is sliced/changed in some way.
 **/
class SummarizerFeature {

	constructor(widgetApi, dashboardApi) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;

		this.logger = dashboardApi.getDashboardCoreSvc('Logger');

		//Identify this feature as a plugin for the resultDataReaderPlugin
		this.resultDataReaderPlugin = true;

		this._activated = false;

		this.overallSummaryValueQuery = new OverallSummaryValueQuery(this.widgetApi, this.dashboardApi, this.logger);

		this.filteredSummaryValueQuery = new FilteredSummaryValueQuery(this.widgetApi, this.dashboardApi, this.logger, /*cbGetRowTuples*/this.getRowTuples.bind(this));

		this.hostAPI = new SummarizerHostAPI(this, widgetApi, dashboardApi);

		this.combinedSummaryValidator = new CombinedSummaryValidator(widgetApi, dashboardApi, this.logger);

		this.hasResultData = false;

		this.rowCountCalcs = {};

		this.isNewQueryApiEnabled = this._isNewQueryApiEnabled();

		this.firstRowDepths = [];

		this.isExpandCollapseEnabled = this._isExpandCollapseEnabled();

		this.hasOlapColumns = false;

	}

	destroy() {
		this.deactivate();
	}

	_isNewQueryApiEnabled() {
		//TODO: This function can be removed when the feature flag is removed.
		return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'queryApi', 'disabled');
	}

	_isExpandCollapseEnabled() {
		return this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'expandCollapse', 'enabled');
	}
	setActivated(flag) {
		this._activated = flag;
	}

	isActivated() {
		return this._activated;
	}

	/**
	 * checks if a chart is supported for stats by checking there is data query in vis definition
	 */
	_isSupported() {
		let isSupported = true;
		if(this.widgetApi && this.widgetApi.getVisApi()){
			const visDefinition  = this.widgetApi.getVisApi().getDefinition();
			if(visDefinition && visDefinition.noDataQuery){
				isSupported = false;
			}
		}
		return isSupported;
	}

	getAPI(){
		return {
			isActivated: this.isActivated.bind(this),
			setActivated: this.setActivated.bind(this),

			getSummaryCollector: this.getSummaryCollector.bind(this),
			getSelectionSummaryCollector: this.getSelectionSummaryCollector.bind(this),

			//ResultDataReader plugin api's
			onNewResults: this.onNewResults.bind(this),

			onDataStart: this.onDataStart.bind(this),
			onDataRow: this.onDataRow.bind(this),
			onDataDone: this.onDataDone.bind(this),

			//host api
			activate: this.activate.bind(this),

			getCombinedSummaryValidator: this.getCombinedSummaryValidator.bind(this),

			createCombinedSummaryValueQuery: this.createCombinedSummaryValueQuery.bind(this)
		};
	}

	activate(options) {
		//Capture events from the event source when a select or clear select happens for 'immediate stats update' on that widget.

		//Capture events from any widget whose selection state has been affected (ie: no new query)
		this.widgetApi.on('visevent:selectionchanged', this.recalculateSelectionData, this);

		this.overallSummaryValueQuery.activate();
		this.filteredSummaryValueQuery.activate();

		this.cbDeactivate = options.cbDeactivate;

		this.queryAPI=this.widgetApi.getVisApi() && this.widgetApi.getVisApi().getQueryAPI();
		if (this.queryAPI) {
			this._resultDataHandler = this.queryAPI.addUnprocessedResultDataHandler(this.processDetailRowCountCalculations.bind(this));
		}

		return this.hostAPI.activate(options);
	}

	deactivate() {

		if(this.isActivated()){
			this.widgetApi.off('visevent:selectionchanged', this.recalculateSelectionData, this);
			if(this.cbDeactivate){
				this.cbDeactivate();
			}
		}

		this.overallSummaryValueQuery.deactivate();
		this.filteredSummaryValueQuery.deactivate();

		if (this.queryAPI) {
			this.queryAPI.removeUnprocessedResultDataHandler(this.processDetailRowCountCalculations.bind(this));
		}

		//When deactivate, set _activated to false;
		this.setActivated(false);
	}

	//Identify this feature as a plugin for the resultDataReaderPlugin
	getFeatureTags() {
		return (this._activated) ? ['ResultDataReaderPlugin'] : [];
	}

	getCombinedSummaryValidator() {
		return this.combinedSummaryValidator;
	}

	/**
	 * A caller calls this function to create a query object to determine combined summary values across a set of widgets.
	 * @param widgetApis {Object[]} - an array of widgetApis to consider combining.
	 * @param isSelectionStats {Boolean} - true if these summaries are for selected data (visible selections should be part of the filter).
	 * @returns the query API for the combined summary query.
	 */
	createCombinedSummaryValueQuery(widgetApis, isSelectionStats) {
		return new CombinedSummaryValueQuery(widgetApis, this.dashboardApi, this.logger, isSelectionStats);
	}

	/**
	 * @returns the 'default stats collector' which contains general measure stats for the data rendered.
	 */
	getSummaryCollector() {
		this.summaryCollector = this.summaryCollector || new SummaryCollector(this.dashboardApi, this.widgetApi, false);
		return this.summaryCollector;
	}

	/**
	 * @returns the 'selections stats collector' which contains measure stats for the selected categories.
	 */
	getSelectionSummaryCollector() {
		this.selectionSummaryCollector = this.selectionSummaryCollector || new SummaryCollector(this.dashboardApi, this.widgetApi, /*selection*/true);
		return this.selectionSummaryCollector;
	}

	/**
	 * row count calculations are used by summarizer when detail averages need to be computed.
	 * This function does the following:
	 * 1) When there are averages (and rowCounts) in a result, map the columnId to its rowCount.
	 * 2) When there are multi-measures in the value slot, the rowCount values are cached by item
	 *		for use later on (see _getDetailRowCount())
	 * @param {object} dssResultData - result data JSON as returned by DSS.
	 * @param {object} options
	 * @param {string} options.layerId - the layer for this data.
	 */
	processDetailRowCountCalculations(dssResultData, options) {
		const visAPI = this.widgetApi.getVisualization();
		const layerId = options.layerId;
		this.cachedRowCounts = {};

		//Do nothing if there are no rowCounts or no summarized items that need them.
		this.rowCountCalcs[layerId] = this._buildRowCountCalculationsMap(dssResultData, layerId, visAPI);
		if (!Object.keys(this.rowCountCalcs[layerId]).length) {
			return;
		}

		const containsMultiMeasure = visAPI.getSlots().getSlotList().find(slot => this._isMultiMeasuresValueSlot(slot));

		if (containsMultiMeasure) {
			Object.keys(this.rowCountCalcs[layerId]).forEach((resultItemId) => {
				this.cachedRowCounts[resultItemId] = [];
				for (let rowIdx=0; rowIdx<dssResultData.data.length; ++rowIdx) {
					const rowCountColumnIndex = this.rowCountCalcs[layerId][resultItemId];
					const rowData = dssResultData.data[rowIdx].pt;
					const rowCountValue = rowData[rowCountColumnIndex];
					this.cachedRowCounts[resultItemId].push(rowCountValue && rowCountValue.v);
				}
			});
		}
	}

	_isMultiMeasuresValueSlot (slotAPI) {
		return slotAPI.getDefinition().isMultiMeasureSupported() && slotAPI.getDataItemList().length > 1;
	}

	/**
	 * recalculateSelectionData is called any time the selection has changed in this widget but only decorations need
	 * to be updated (ie: a change of selection in one widget causes a change in selection in another with no change in
 	 * its query).  In this case, the target widget will send a widget level event notifying of the change.
	 * We need to reprocess the same results with the updated selections and notify the hostApp of the update.
	 * NOTE: This function is only needed for "other widgets" in cases where their query didn't change (aka no re-render)
	 * @param payload - the event payload which includes information about whether this widget was the originator of the event
	 */
	recalculateSelectionData(/*payload*/) {
		if (this.queryResultDataByLayer && this.rowHeadersByLayer) {
			//Reprocess the values...
			Object.keys(this.rowHeadersByLayer).forEach((layerId) => {
				const queryResultData=this.queryResultDataByLayer[layerId];
				const rowHeaders=this.rowHeadersByLayer[layerId];
				if (rowHeaders && queryResultData) {
					const rowCount=this._getRowCount(queryResultData);
					this.onDataStart(queryResultData, rowHeaders);
					for (let rowIdx=0; rowIdx<rowCount; rowIdx+=rowHeaders.valueSlotMeasureCount) {
						this.onDataRow(rowIdx);
					}
					//Whether re-processing the values or not, we need to update the last category selection.
					this.updateLastCategorySelected();
					this.filteredSummaryValueQuery.execute(this.selectionSummaryCollector).then(() => {
						this.hostAPI.updateSelectionSummaries();
					});
				}
			});
			this._forceRecalculateSelections = false;
		}
	}

	getSelectedContent() {
		return this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
	}

	updateLastCategorySelected() {
		//The last category selection will be the last selected tuple.
		const content = this.getSelectedContent();
		const controller = content.getFeature('InteractivityController.deprecated');
		const selectedTuples = controller.getSelectedTuples(this.categoryItemHeaders.itemIds);
		if (selectedTuples && selectedTuples.length) {
			//The lastTuple in the set of selected tuples could be an array or a single value.
			//The single value (or an array entry) could be null (no value).
			const lastTuple = this.findLastSelectedTupleInVisualization(selectedTuples);
			if (lastTuple) {
				this._setLastSelectedTuple(lastTuple);
			}
		}
	}

	_setLastSelectedTuple(lastTuple) {
		if (Array.isArray(lastTuple)) {
			this.selectionSummaryCollector.setLastCategorySelection(lastTuple.map((tuplePart) => (tuplePart) ? tuplePart.d: tuplePart));
		} else {
			this.selectionSummaryCollector.setLastCategorySelection([(lastTuple) ? lastTuple.d : lastTuple]);
		}
	}

	//Reverse-search the array of selectedTuples for the last one that occurs in this visualization.
	findLastSelectedTupleInVisualization(selectedTuples) {
		for (let i=selectedTuples.length-1; i>=0; --i) {
			if (this.selectionSummaryCollector.isTupleInSelectionSummary(selectedTuples[i])) {
				return selectedTuples[i];
			}
		}
		return null;
	}

	/**
	 * This event is called at the very beginning of a render with new data
	 * or a render of placeholder without data( i.e. when the viz placeholder
	 * is rendered, this function is still called with empty resultData. When
	 * resultData is empty, onDataStart, onDataRow and onDataEnd won't be run)
	 *
	 * It is similar to onDataStart but allows for process initialization
	 * to be separated from onDataStart
	 */
	onNewResults(resultData) {
		//When new data is rendered, initialize rowHeader and result pointers (one per layer).
		this.rowHeadersByLayer={};
		this.queryResultDataByLayer={};
		this.queryResultData=null;
		let returnPromise = Promise.resolve({});
		const hasResultData = this._getResultHasData(resultData);
		if (!this._isSupported()) {
			return returnPromise;
		}

		this._resetSummaryCollectors();

		if (this.hasResultData && !hasResultData) {
			//Deleting a slot case:
			//resultData is not empty previously, but new returned resultData
			//is empty. In this case, reset and update summary table
			returnPromise = this.hostAPI.updateSummaries().then(() => {
				this.selectionSummaryCollector.setLastCategorySelection([]);
				this.hostAPI.updateSelectionSummaries();
			});
		}
		this.hasResultData=hasResultData ? true : false;
		if (this.isNewQueryApiEnabled && resultData.getQueryResultIdList) {
			resultData.getQueryResultIdList().forEach((layerId) => {
				const queryResult = resultData.getResult(layerId);
				const dssQueryResultString = queryResult && queryResult.getPropertyValue('RawData.internal');
				const dssQueryResult = dssQueryResultString && JSON.parse(dssQueryResultString);
				this.processDetailRowCountCalculations(dssQueryResult, { layerId: layerId });
			});
		}

		return returnPromise || Promise.resolve(true);
	}

	/**
	 * Function run at start-of-data.
	 * @param {Object} queryResultData
	 * @param {function} rowHeaders - a structure describing the category and measure (ordinal) row Headers and their positions in the row.
	 */

	onDataStart(queryResultData, rowHeaders) {
		this.visAPI = this.widgetApi.getVisApi();

		//onDataStart/onDataRow/OnDataDone is called once for each layer (usually 1)
		this.activeLayerId = rowHeaders.layerId;

		//Save results and headers by layer for recalculateSelectionData...
		this.queryResultDataByLayer=this.queryResultDataByLayer || {};
		this.rowHeadersByLayer=this.rowHeadersByLayer || {};
		this.queryResultDataByLayer[rowHeaders.layerId]=queryResultData;
		this.rowHeadersByLayer[rowHeaders.layerId]=rowHeaders;

		this.queryResultData=queryResultData;

		//TODO: This special logic for setting the domain of a waterfall chart will change when the VIDA api is ready (RTC258118)
		//A waterfall chart is much like a single column stacked chart (where the grouping column is always 0)
		this.isWaterfallStack = (this.visAPI.getVisId() === 'com.ibm.vis.rave2bundlewaterfall');

		this.widgetContentApi = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());

		//Is there a stacked summary slot that is populated with items?
		const stackedSummarySlot = this.widgetContentApi.getFeature('Visualization').getSlots().getMappedSlotList().find((slot) => {
			return (this.isWaterfallStack) ? slot : slot.getDefinition().getProperty('stackValuesOnAxis');
		});

		//Is it a regular item or a stacked Multi Measure item?
		this.stackedMultiMeasures = !!(stackedSummarySlot && this._isMultiMeasuresSeriesSlot(stackedSummarySlot));
		this.stackedSummaries = this.isWaterfallStack || !!(stackedSummarySlot && !this.stackedMultiMeasures);

		this._resetSummaryCollectors(rowHeaders.layerId);

		this.valueSlotMeasureCount = rowHeaders.valueSlotMeasureCount;
		this.measureItemHeaders = rowHeaders.measureItemHeaders;
		this.categoryItemHeaders = rowHeaders.categoryItemHeaders;

		this.visSelected = this._getSelected(this.categoryItemHeaders);

		//get all mapped dataitems
		const mappedItems = this.widgetContentApi.getFeature('Visualization').getSlots().getMappingInfoList();
		mappedItems.forEach(item => {
			if(item.dataItem.getMetadataColumn() && item.dataItem.getMetadataColumn().isOlapColumn()){
				this.hasOlapColumns = true;
			}
		});
	}

	_isMultiMeasuresSeriesSlot (slotAPI) {
		const dataItemList = slotAPI.getDataItemList();
		return dataItemList.find(dataItem => {
			return dataItem.getId() === '_multiMeasuresSeries';
		}) ? true : false;
	}

	/**
	 * function run on each data row
	 * @param rowIdx - the row index
	 */
	onDataRow(rowIdx) {
		let rowSummaries=[];

		//This local variable is only used for stacked charts (stacked area/bar/column)
		//when the components of the stack are measures (in this case, the stacked summary value is the total of all measure values in the stack)
		let multiMeasuresStackedSummaryValue = 0;

		let skipRow = false;
		if(this.queryResultData.getVersion && this.queryResultData.getVersion() === '2'){
			const rowTuple = this._getRowTuple(rowIdx, this.categoryItemHeaders);
			if(this.hasOlapColumns && this.isExpandCollapseEnabled){
				if(rowIdx === 0){
					this.firstRowDepths = rowTuple.map(tuple => tuple['ln']);
				} else {
					rowTuple.forEach(tuple => {
						this.firstRowDepths.forEach(depthOfCategory => {
							skipRow = skipRow || (tuple.ln > depthOfCategory || tuple.type === this.queryResultData.VALUE_TYPE.SUMMARY);
						});
					});
				}
			} else {
				skipRow = skipRow || this.queryResultData.VALUE_TYPE && rowTuple.find(tuple => tuple.type === this.queryResultData.VALUE_TYPE.SUMMARY);
			}
		}

		this.measureItemHeaders.forEach((measureItemHeader) => {
			const value = this._getMeasureCellValue(rowIdx, measureItemHeader);
			const detailRowCount = this._getDetailRowCount(rowIdx, measureItemHeader);
			if(!skipRow){
				rowSummaries.push({ dataItemUniqueId: measureItemHeader.dataItemUniqueId, value: value, detailRowCountUse: detailRowCount });

				let itemSummaryEntry=this.summaryCollector.getSummary(measureItemHeader.dataItemUniqueId);
				this.summaryCollector.updateSummary(itemSummaryEntry, value, detailRowCount);
				if (this.stackedSummaries) {
					this._applyStackedSummaries(rowIdx, itemSummaryEntry, measureItemHeader, value);
				} else if (this.stackedMultiMeasures) {
					//This is a measure stack so we need the total of all of the measure values in the stack.
					multiMeasuresStackedSummaryValue += value;
				}
			}
		});

		if (this.stackedMultiMeasures) {
			//If this is a measure in a measure stack, AND this is a stacked chart (stacked column/bar/area),
			//apply the same stacked summary value to all measure entries (it will become the domain of the value item when applicable)
			this.measureItemHeaders.forEach((measureItemHeader) => {
				if (measureItemHeader.multiMeasuresValueItem) {
					let itemSummaryEntry=this.summaryCollector.getSummary(measureItemHeader.dataItemUniqueId);
					this._applyStackedSummaries(rowIdx, itemSummaryEntry, measureItemHeader, multiMeasuresStackedSummaryValue);
				}
			});
		}

		this._processSelectionsForRow(rowIdx, rowSummaries);
	}

	_applyStackedSummaries(rowIdx, itemSummaryEntry, measureItemHeader, value) {
		const rowDataArray = this._getRowDataArray(this.queryResultData);
		const rowData = rowDataArray && rowDataArray[rowIdx + measureItemHeader.valueItemIndex];
		//For all Stacked visualizations we support, the grouping column is the first (the category)
		//The grouping column is categorical so the row data will store the value index (have values from 0-n)
		//A waterfallStack is a special case...it is like a single stack where the values from row0..n result in a single sum (groupId=0)
		const groupingColumnValueId = this.isWaterfallStack ? 0 : (rowData && rowData.pt && rowData.pt[0]);
		if (groupingColumnValueId>=0) {
			this.summaryCollector.updateStackedSummary(itemSummaryEntry, value, groupingColumnValueId);
		}
	}

	_getMeasureCellValue(rowIdx, measureItemHeader, columnIndex = measureItemHeader.index) {
		//rowIdx is normally 1:1 with the row of data....so any measure cell value is found at row=rowIdx, column=slotIndex+indexInSlot
		//For multiMeasureValue slots (valueSlotMeasureCount is > 1),the data is returned as "rowgroups" where
		//rowIdx is a group index and groupX, rowNInTheGroup represents the row data plus the Nth measure in the value slot.
		//In this case, for 2 measures, this function gets called with rowIdx,valueItemIndex (0, 0), (0, 1), (2, 0), (2, 1)

		const measureCellValue = (this.valueSlotMeasureCount > 1) ?
			this._getCellValue(this.queryResultData, rowIdx + measureItemHeader.valueItemIndex, columnIndex) :
			this._getCellValue(this.queryResultData, rowIdx, columnIndex + measureItemHeader.valueItemIndex);
		return measureCellValue === '' ? 0 : measureCellValue;
	}

	//TODO: Remove this function when  isNewQueryApiEnabled is removed
	_getCellValueObject(queryResult, row, column) {
		return this.isNewQueryApiEnabled ? queryResult.getValue(row, column) : queryResult.getCellValue(row, column);
	}

	_getCellValue(queryResult, row, column) {
		const cellValueObject = this._getCellValueObject(queryResult, row, column);
		if (this.isNewQueryApiEnabled) {
			return (cellValueObject && cellValueObject.getValue) ? cellValueObject.getValue() : cellValueObject.value;
		} else {
			return (cellValueObject && cellValueObject.getValue) ? cellValueObject.getValue() : cellValueObject.v;
		}
	}

	//DETAIL ROWCOUNT SUPPORT....
	_getRowCount(queryResultData) {
		return this.isNewQueryApiEnabled ?
			queryResultData.getRowCount() :
			queryResultData.getDatapointCount();
	}

	_getRowDataArray(queryResultData) {
		return (this.isNewQueryApiEnabled) ?
			queryResultData.getDataPointValueList() :
			queryResultData.getDatapoints();
	}

	_getResultHasData(resultData) {
		return (this.isNewQueryApiEnabled && resultData.getResult) ||
			(!this.isNewQueryApiEnabled && resultData.getQueryResults);
	}

	//NOTE: ResultDataReader plugins (including Summarizer) currently are given the old result API (queryResultData).
	//A number of methods will change (slightly) when the new queryResultAPI is available

	//return the detail rowCount associated with this measure for row X (or NO_DETAIL_ROWCOUNT if none exists)
	_getDetailRowCount(rowIdx, measureItemHeader) {
		const layerId = this.activeLayerId;
		const rowCountIdx = this.rowCountCalcs && this.rowCountCalcs[layerId] && this.rowCountCalcs[layerId][measureItemHeader.dataItemUniqueId];
		if (measureItemHeader.aggregate!=='avg' || rowCountIdx===undefined) {
			//If there's no rowcount for this item or it doesn't apply to this aggregation type, return 'NO_DETAIL_ROWCOUNT' value (-1)
			return SummaryCollector.NO_DETAIL_ROWCOUNT;
		}
		const cacheDetailRow = Math.floor(rowIdx / this.valueSlotMeasureCount);
		const cachedRowCountsForItem = this.cachedRowCounts && this.cachedRowCounts[measureItemHeader.dataItemUniqueId];
		if (cachedRowCountsForItem && cachedRowCountsForItem[cacheDetailRow]) {
			return cachedRowCountsForItem[cacheDetailRow];
		}
		return this._getMeasureCellValue(rowIdx, measureItemHeader, rowCountIdx);
	}

	//Create a mapping between measure ids and the column indexes where rowCount data is found (for the active layer).
	//This function will compute the rowCounts only if the data items contains Averages.
	_buildRowCountCalculationsMap(dssResultData, layerId) {
		let rowCountCalcs = {};
		let measureDataItems = [];
		//We only need to compute detail averages for visualizations if they include an average.
		let containsAverage = false;

		const widgetContentApi = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const mappedItems = widgetContentApi.getFeature('Visualization').getSlots().getMappingInfoList();
		mappedItems.forEach((item) => {
			if(item.slot.getDefinition().getDatasetIdList().some((dataSetId) => dataSetId === layerId) && item.dataItem.getAggregation() === 'avg' ) {
				containsAverage = true;
				if(!dssResultData.dataItems){
					measureDataItems.push(item.dataItem.getId());
				}
			}
		});

		if (containsAverage) {
			let resultItemMap = {};
			if(dssResultData.dataItems){
				dssResultData.dataItems.forEach((resultItem, idx) => { resultItemMap[resultItem.itemClass.id] = idx; });
				rowCountCalcs = this._computeRowCountCalcs(resultItemMap);
			} else {
				//For multi-edge, all measures and calculations are on the same edge (usually edge 1 or 2)
				const measureEdgeIndex = dssResultData.edges.findIndex((edge) => {
					return edge.itemClasses && edge.itemClasses.some((itemClass) => {
						return itemClass.h && itemClass.h[0] &&
							measureDataItems.some((measureDataItem) => measureDataItem === itemClass.h[0].di);
					});
				});
				if (measureEdgeIndex >= 0) {
					//Extract the di's from the itemClasses on the measure edge
					const measureItemIds = dssResultData.edges[measureEdgeIndex].itemClasses.map(itemClass => itemClass.h[0]);
					measureItemIds.forEach((item, index) => { resultItemMap[item.di] = measureEdgeIndex + index; });
					rowCountCalcs = this._computeRowCountCalcs(resultItemMap);
				}
			}
		}
		return rowCountCalcs;
	}

	//Given a map of itemIds keyed to their indexes { 'rowCount:id1': 2, 'rowCount:id2': 6}
	//Return a map keyed by "baseId" to the corresponding rowCount index: rowCountMap: { 'id1': 2, 'id2': 6 }
	_computeRowCountCalcs(resultItemMap) {
		let rowCountCalcs = {};
		const resultItemMapKeys = Object.keys(resultItemMap);
		resultItemMapKeys.forEach((dataItemId) => {
			const rowCountIdx = resultItemMap['rowCount:' + dataItemId];
			if (rowCountIdx!==undefined) {
				rowCountCalcs[dataItemId] = rowCountIdx;
			}
		});
		return rowCountCalcs;
	}

	/**
	 * function run at end of data...this function returns a promise.
	 * @param renderContext - the renderContext as used in the render sequence.
	 * @returns a promise
	*/
	onDataDone(renderContext) {
		const rowCount = this._getRowCount(this.queryResultData);

		this.measureItemHeaders.forEach((measureItemHeader) => {
			let itemSummaryEntry = this.summaryCollector.getSummary(measureItemHeader.dataItemUniqueId);

			this.summaryCollector.finalizeSummaries(itemSummaryEntry, rowCount / this.valueSlotMeasureCount, measureItemHeader.aggregate);
		});

		const valuesNeeded = this.summaryCollector.areOverallSummaryValuesNeeded();
		if (valuesNeeded) { // wiring off until query work is complete
			//Augment the summary info with the query results from a query for unfiltered measures.
			return this.overallSummaryValueQuery.execute(this.summaryCollector).then(() => {
				return this.filteredSummaryValueQuery.execute(this.summaryCollector).then(() => {
					return this.filteredSummaryValueQuery.execute(this.selectionSummaryCollector).then(() => {
						return this._updateSummaries(renderContext);
					});
				});
			});
		}

		return this._updateSummaries(renderContext);
	}

	_resetSummaryCollectors(layerId=null){
		this.summaryCollector = this.getSummaryCollector();
		this.selectionSummaryCollector = this.getSelectionSummaryCollector();
		this.summaryCollector.reset(layerId);
		this.selectionSummaryCollector.reset(layerId);
	}

	_updateSummaries(renderContext) {
		return this.hostAPI.updateSummaries().then((topBottomSpec) => {
			//NOTE: Selection summaries cb (when selections exist) are expected after the default summaries cb.
			this.updateLastCategorySelected();
			this.hostAPI.updateSelectionSummaries();
			// make sure topBottomSpec is a truthy Object
			if (topBottomSpec && typeof topBottomSpec === 'object') {
				if (this.isNewQueryApiEnabled) {
					const contentAPI = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
					const queryExecutionAPI = contentAPI && contentAPI.getFeature('DataQueryExecution.internal');
					queryExecutionAPI && queryExecutionAPI.setMinMaxQueryResults(topBottomSpec[this.widgetApi.getId()]);
				} else if (renderContext) {
					renderContext.data.setTopBottomMappings &&
						renderContext.data.setTopBottomMappings(topBottomSpec[this.widgetApi.getId()], 0 /*query result index*/);
				}
			}
			return {};
		});
	}

	_getSelected(categoryItemHeaders) {
		//Get the visualization selected api but add the option to ignoreEdgeSelections.
		//We need to handle edge selections differently to give more appropriate summary totals when multiple edges intersect.
		//Even though the visualization highlights the union of intersected edges, the intersection values make more sense.
		//In future, this behaviour may be incorporated into the visSelected object.
		const content = this.getSelectedContent();
		const dataPointSelections = content.getFeature('DataPointSelections.deprecated');
		return dataPointSelections.getSelection(categoryItemHeaders.itemIds, { ignoreEdgeSelections: true });
	}

	//Process the categorical items in a row returning their values as an array... eg [2005, Camping, Fax]
	_getRowTuple(rowIdx, categoryItemHeaders) {
		let tuple=[];
		categoryItemHeaders.columnIndexes.forEach((columnIndex) => {
			//Note: getValue returns the tuple of categorical values at that cell index for stacked items.
			let cellValue = this._getCellValueObject(this.queryResultData, rowIdx, columnIndex);
			if (cellValue && cellValue.type){
				cellValue = cellValue.map(value => ({ ...value, type: cellValue.type }));
			}
			tuple = tuple.concat(cellValue);
		});
		return tuple;
	}

	/**
	 * Return the categorical information rendered in a visualization.
	 * @param {string} [selectionsOnly=false] - only include the selected rows.
	 * @param {string} [layerId='data'] - the layer for this query result.
	 * @returns the categorical data tuples and item Ids for the rendered rows (or selected rows)
	 */
	getRowTuples(selectionsOnly=false, layerId='data') {
		const tuples=[];
		const rowHeaders = this.rowHeadersByLayer[layerId];
		const queryResult = this.queryResultDataByLayer[layerId];
		if (queryResult && rowHeaders && rowHeaders.categoryItemHeaders) {
			const rowCount=this._getRowCount(queryResult);
			for (let rowIdx=0; rowIdx<rowCount; rowIdx+=rowHeaders.valueSlotMeasureCount) {
				if (!selectionsOnly || this._processSelectionsForRow(rowIdx)) {
					tuples.push(this._getRowTuple(rowIdx, rowHeaders.categoryItemHeaders));
				}
			}
		}

		//At this point, we have a parallel array of itemIds and tuple part use/display values.
		//If one of the itemIds is multiMeasuresSeries, strip it and the corresponding tuplepart out.
		//The value is an itemLabel (like Revenue) not a data value (like 2004)
		let itemIds = rowHeaders && rowHeaders.categoryItemHeaders && rowHeaders.categoryItemHeaders.itemIds || [];
		const multiMeasuresIndex = itemIds.indexOf('_multiMeasuresSeries');
		if (multiMeasuresIndex!==-1) {
			itemIds.splice(multiMeasuresIndex, 1);
			tuples.forEach((tuple) => tuple.splice(multiMeasuresIndex, 1));
		}
		return {
			itemIds: itemIds,
			tuples: tuples
		};
	}

	//Return true if this row is selected.  If rowSummaries are passed, update the selectionSummaryCollector with the values.
	_processSelectionsForRow(rowIdx, rowSummaries=null) {
		//for each row, if the categorical tuple for that row is selected, add it to selection summaries.
		const tuple=this._getRowTuple(rowIdx, this.categoryItemHeaders);
		//FIRST, process any edge selections.
		let isSelected = this._processEdgeSelectionsForRow(tuple, rowSummaries);
		//THEN, process datapoint selections.
		if (this.visSelected && this.visSelected.isSelected(tuple)) {
			if (rowSummaries) {
				rowSummaries.forEach((rowSummary) => {
					let selectionEntry = this.selectionSummaryCollector.getSummary(rowSummary.dataItemUniqueId);
					this.selectionSummaryCollector.updateSelectionSummary(selectionEntry, rowSummary, tuple);
				});
				//Handle cases where selections are not an exact match
				//(eg: in chartA, 2004 is selected but chartB projects Year.Country.
				//means that in chartB 2004.Canada, 2004.USA, 2004.France etc are all selected tuples.
				this._setLastSelectedTuple(tuple, rowSummaries);
			}
			isSelected = true;
		}
		return isSelected;
	}

	//Find the intersected result of any edge selections.
	_processEdgeSelectionsForRow(tuple, rowSummaries) {
		//Edge selections are analagous to selecting N values from 1 item, then M values from another etc.
		//Tables are 'edge selectors', outer crosstab labels, legend/axis selections are other examples.
		//Edge example A: Camping, Golf, then B: 2004, 2005, 2006
		//(They are 'symmetrical' meaning you Cant have 2 values of A without all 3 values of B)

		//Use the Selection Controller rather than visSelected for this.
		const content = this.getSelectedContent();
		const selectionCtrlr = content.getFeature('InteractivityController.deprecated');

		if (!selectionCtrlr) {
			return false;
		}

		//RULE: Values for this tuple should be part of the total IF the value for that tuple part is selected OR
		//there are no selections for that item in the tuple.
		const someItemHasSelections = this.categoryItemHeaders.itemIds.find((itemId) => {
			return selectionCtrlr.isItemSelected(itemId);
		});

		if (someItemHasSelections) {
			const thisTupleIsNotSelected=tuple.find((tuplePart, ci) => {
				const itemId = this.categoryItemHeaders.itemIds[ci];
				return (selectionCtrlr.isItemSelected(itemId) && !selectionCtrlr.isValueSelected(itemId, tuplePart));
			});

			if (!thisTupleIsNotSelected) {
				if (rowSummaries) {
					rowSummaries.forEach((rowSummary) => {
						let selectionEntry = this.selectionSummaryCollector.getSummary(rowSummary.dataItemUniqueId);
						this.selectionSummaryCollector.updateSelectionSummary(selectionEntry, rowSummary, tuple);
					});
					this._setLastSelectedTuple(tuple);
				}
				return true;
			}
		}
		return false;
	}
}

export default SummarizerFeature;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/SummarizerFeature.js